import FormLayout from 'src/autobahn/layouts/form-layout';
import * as Label from '@radix-ui/react-label';
import * as Form from '@radix-ui/react-form';
import sharedStyles from 'src/autobahn/shared/styles.module.scss';
import classNames from 'classnames';
import type MaintenanceModel from 'src/core/models/maintenanceModel';
import Select from 'src/autobahn/components/select';
import {useCompleteMaintenance, useRealizeMaintenance} from './queries';
import Yup from 'src/infra/Yup';
import {Controller, FieldErrors, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {useMemo} from 'react';
import {Field} from 'src/autobahn/components/form/field';
import {TextareaInput} from 'src/autobahn/components/input/textarea-input';
import {NumberInput} from 'src/autobahn/components/input/number-input';
import {DateInput} from 'src/autobahn/components/input/date-input';
import {useIntl} from 'react-intl';

type MaintenanceFormData = {
  cost: number;
  description: string;
  status: 'pending' | 'inProgress' | 'completed';
  nextOdometer?: number;
  nextDate?: Date;
  nextHourmeter?: number;
};

interface MaintenanceRealizeFormProps {
  selectedMaintenance: MaintenanceModel;
  onCancel: () => void;
}

const MaintenanceRealizeForm = ({
  selectedMaintenance,
  onCancel,
}: MaintenanceRealizeFormProps) => {
  const intl = useIntl();

  const currentAcceptedStatus = useMemo(() => {
    const currentStatus = selectedMaintenance.status;
      switch (currentStatus) {
        case 'pending':
          return ['inProgress', 'completed'];
        case 'inProgress':
          return ['completed'];
        case 'completed':
          return [];
        default:
          return [];
      }
  }, [selectedMaintenance.status]);

  const {schema, defaultValues} = useMemo(() => {
    const shape: {[key: string]: Yup.AnySchema} = {
      cost: Yup.number()
        .transform((value) => isNaN(value) ? undefined : value)
        .label(intl.formatMessage({id: 'sharedCost'}))
        .min(0, intl.formatMessage({id: 'sharedMinZero'}))
        .typeError(intl.formatMessage({id: 'sharedInvalidNumber'}))
        .required(),
      description: Yup.string()
        .label(intl.formatMessage({id: 'sharedDescription'}))
        .required(),
      nextOdometer: Yup.number()
        .transform((value) => isNaN(value) ? undefined : value)
        .label(intl.formatMessage({id: 'sharedOdometer'}))
        .typeError(intl.formatMessage({id: 'sharedInvalidNumber'}))
        .notRequired(),
      nextDate: Yup.date()
        .label(intl.formatMessage({id: 'sharedDate'}))
        .typeError(intl.formatMessage({id: 'sharedDateInvalid'}))
        .notRequired(),
      nextHourmeter: Yup.number()
        .transform((value) => isNaN(value) ? undefined : value)
        .label(intl.formatMessage({id: 'sharedHourmeter'}))
        .typeError(intl.formatMessage({id: 'sharedInvalidNumber'}))
        .notRequired(),
    };

    shape.status = Yup.string()
      .oneOf(currentAcceptedStatus)
      .label(intl.formatMessage({id: 'customerStatus'}))
      .required();


    const defaultValues: Partial<MaintenanceFormData> = {
      description: selectedMaintenance.description!,
      status: selectedMaintenance.status!,
      nextDate: new Date(),
    };

    if (selectedMaintenance.cost) {
      defaultValues.cost = selectedMaintenance.cost;
    }

    if (selectedMaintenance.currentOdometer) {
      defaultValues.nextOdometer = selectedMaintenance.currentOdometer;
    }

    if (selectedMaintenance.nextDate) {
      defaultValues.nextDate = new Date(selectedMaintenance.nextDate);
    }

    if (selectedMaintenance.currentHourmeter) {
      defaultValues.nextHourmeter = selectedMaintenance.currentHourmeter;
    }

    const _schema = Yup.object().shape(shape) as unknown as Yup.ObjectSchema<MaintenanceFormData>;

    return {
      schema: _schema,
      defaultValues,
    };
  }, [selectedMaintenance, currentAcceptedStatus]);

  const {register, control, handleSubmit} = useForm<MaintenanceFormData>({
    resolver: yupResolver(schema, {abortEarly: false}),
    defaultValues,
  });

  const {mutate: updateMaintenance} = useRealizeMaintenance();

  const {mutate: completeMaintenance} = useCompleteMaintenance();

  const onDataValid = ({nextDate, ...data}: MaintenanceFormData) => {
    const formData = {
      id: selectedMaintenance.id!,
      deviceId: selectedMaintenance.deviceId!,
      maintenanceId: selectedMaintenance.maintenanceId!,
    };

    const date = nextDate ? nextDate.toISOString() : undefined;

    if (data.status === 'completed') {
      completeMaintenance({
        ...formData,
        cost: data.cost,
        description: data.description,
        status: 'completed',
        nextOdometer: data.nextOdometer,
        nextHourmeter: data.nextHourmeter,
        // should send date too
        // @ts-ignore
        date,
      }, {
        onSuccess: () => {
          onCancel();
        },
      });
    } else {
      updateMaintenance({
        ...formData,
        ...data,
        date,
      }, {
        onSuccess: () => {
          onCancel();
        },
      });
    }
  };

  const onDataInvalid = (_data: FieldErrors<MaintenanceFormData>) => {};

  return (
    <FormLayout
      title={intl.formatMessage({id: 'maintenanceRealizeFormTitle'})}
      onCancel={onCancel}
      onSubmit={handleSubmit(onDataValid, onDataInvalid)}
    >
      <div style={{display: 'flex', flex: 2, flexDirection: 'row'}}>
        <div className={sharedStyles.formField} style={{flex: 1, justifyContent: 'flex-start'}}>
          <span className={classNames(sharedStyles.formFieldLabel, sharedStyles.formFieldLabelSpacingBottom)}>{intl.formatMessage({id: 'sharedTitle'})}</span>
          <span className={sharedStyles.formFieldLabelLight}>{selectedMaintenance.title}</span>
        </div>

        <div className={sharedStyles.formField} style={{flex: 1, justifyContent: 'flex-start'}}>
          <span className={classNames(sharedStyles.formFieldLabel, sharedStyles.formFieldLabelSpacingBottom)}>{intl.formatMessage({id: 'sharedVehicle'})}</span>
          <span className={sharedStyles.formFieldLabelLight}>{selectedMaintenance.vehicle}</span>
        </div>
      </div>

      <Label.Root asChild>
        <span className={classNames(sharedStyles.formFieldLabel, sharedStyles.formFieldLabelSpacingBottom)} style={{display: 'block', color: '#242424'}}>{intl.formatMessage({id: 'sharedKind'})} / {intl.formatMessage({id: 'sharedPeriod'})}</span>
      </Label.Root>

      <div style={{display: 'flex', flex: 1}}>
        <div style={{display: 'flex', flex: 0.5, flexDirection: 'column', rowGap: '12px'}}>
          {!!selectedMaintenance.currentOdometer && (
            <Field
              name='nextOdometer'
              type='number'
              label={intl.formatMessage({id: 'sharedOdometer'})}
              horizontal
              labelKind='light'
              // @ts-ignore
              style={{justifyContent: 'space-between'}}
            >
              <NumberInput
                {...register('nextOdometer')}
              />
            </Field>
          )}

          <Field
              name='nextDate'
              type='number'
              label={intl.formatMessage({id: 'sharedDay'})}
              horizontal
              labelKind='light'
              // @ts-ignore
              style={{justifyContent: 'space-between'}}
            >
              <Controller
                control={control}
                name='nextDate'
                render={({field}) => (
                  <DateInput
                    {...field}
                    displayFormat='dd/MM/yyyy'
                    placeholder={`${intl.formatMessage({id: 'sharedDay'})} ...`}
                  />
                )}
              />
            </Field>

          {!!selectedMaintenance.currentHourmeter && (
            <Field
              name='nextHourmeter'
              type='number'
              label={intl.formatMessage({id: 'sharedHourmeter'})}
              horizontal
              labelKind='light'
              // @ts-ignore
              style={{justifyContent: 'space-between'}}
            >
              <NumberInput
                {...register('nextHourmeter')}
              />
            </Field>
          )}

          <Field
            name='cost'
            type='number'
            label={intl.formatMessage({id: 'sharedCost'})}
          >
            <NumberInput
              {...register('cost')}
            />
          </Field>
        </div>
      </div>

      <div style={{display: 'flex', flexDirection: 'column'}}>
        <Field
          name='description'
          type='textarea'
          label={intl.formatMessage({id: 'sharedDescription'})}
        >
          <TextareaInput
            {...register('description')}
          />
        </Field>
      </div>

      <div style={{display: 'flex', flex: 1}}>
        <div style={{display: 'flex', flex: 0.5, flexDirection: 'column', rowGap: '12px'}}>
          <Form.Field name='status' className={classNames(sharedStyles.formField, sharedStyles.noBottomMargin)}>
            <Form.Label className={classNames(sharedStyles.formFieldLabel)}>{intl.formatMessage({id: 'customerStatus'})}</Form.Label>
            <Form.Control asChild>
              <Controller
                control={control}
                name='status'
                render={({field}) => {
                  return (
                    <Select
                      {...field}
                      items={[
                        {label: intl.formatMessage({id: 'maintenanceStatusPending'}), value: 'pending', disabled: !currentAcceptedStatus.includes('pending')},
                        {label: intl.formatMessage({id: 'maintenanceStatusOngoing'}), value: 'inProgress', disabled: !currentAcceptedStatus.includes('inProgress')},
                        {label: intl.formatMessage({id: 'maintenanceStatusCompleted'}), value: 'completed', disabled: !currentAcceptedStatus.includes('completed')},
                      ]}
                    />
                  );
                }}
              />
            </Form.Control>
          </Form.Field>
        </div>
      </div>
    </FormLayout>
  );
};

export default MaintenanceRealizeForm;
